<template>
    <v-app v-if="isBetaBlocked">
        <v-main class="background-app">
            <beta-gate />
        </v-main>
    </v-app>
    <v-app v-else-if="(hasPaymentFailed || hasNoPlan) && !acceptAccessRoutes">
        <v-main class="background-app">
            <disabled-account />
        </v-main>
    </v-app>
    <v-app v-else-if="needUpdateApp">
        <update-app />
    </v-app>
    <v-app v-else :class="appClass">
        <toolbar :show-toolbar="toolbar" />
        <navigation-drawer v-if="drawer && !isMobile" />
        <admin-navigation-drawer v-if="adminDrawer && isAdminUser && !isMobile" />
        <component :is="rightPanel" v-if="rightPanel && !isMobile" />
        <v-main class="background-app" :class="{ 'main-safe-bottom': isMobile && drawer && !footer }">
            <transition name="main">
                <v-container fluid :class="[{ 'no-padding': noPaddingRoutes }]">
                    <v-row no-gutters>
                        <v-col cols="12">
                            <router-view></router-view>
                        </v-col>
                    </v-row>
                </v-container>
            </transition>
        </v-main>
        <bottom-navigation v-if="footer && isMobile" />
        <admin-shortcut v-if="isHomeRoute && isAdminUser" />
        <kimsa-toast />
        <kimsa-install-toast />
        <kimsa-update-toast />
        <overlay-reconnect />
        <overlay-update />
        <survey-active-dialog v-if="currentUser" />
        <plan-required-dialog />
        <dialog-back-handler />
        <beta-badge />
        <history-toast />
    </v-app>
</template>

<script>
import Toolbar from "@/components/common/Toolbar";
import NavigationDrawer from "@/components/common/NavigationDrawer";
import AdminNavigationDrawer from "@/components/common/AdminNavigationDrawer";
import AdminShortcut from "@/components/admin/AdminShortcut";
import HomeAgendaPanel from "@/components/coach/HomeAgendaPanel";
import CompanyHomePanel from "@/components/company/home/CompanyHomePanel";
import CompanyAdminPanel from "@/components/company/CompanyAdminPanel";
import StudentsChatPanel from "@/components/coach/StudentsChatPanel";
import BottomNavigation from '@/components/common/BottomNavigation';
import KimsaToast from "@/components/common/KimsaToast";
import KimsaInstallToast from "@/components/common/KimsaInstallToast";
import KimsaUpdateToast from "@/components/common/KimsaUpdateToast";
import OverlayReconnect from '@/components/common/OverlayReconnect';

import { LocalNotifications } from '@capacitor/local-notifications';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import StatusBarManager from '@/utils/StatusBarManager';
import { APP_SCHEMES } from '@/utils/Platform';

import firebase from 'firebase/app'
import 'firebase/auth'
import { Device, Notifications, Screen } from "@/utils/Capacitor";
import { saveDeviceToken } from "@/utils/PushDevices";
import { Storage } from "@/utils/Storage";
import { db } from "@/utils/Firebase";
import { applyLocale, detectDeviceLocale } from "@/utils/Locale";
import { getStoredLocale } from "@/plugins/i18n";
import { skipsBilling } from "@/utils/Helpers";
import { resolveNotificationRoute } from "@/utils/Routing";

import { mapState, mapMutations, mapActions } from 'vuex'
import DisabledAccount from "@/components/common/DisabledAccount";
import OverlayUpdate from "@/components/common/OverlayUpdate";
import UpdateApp from "@/components/common/UpdateApp";
import SurveyActiveDialog from "./components/common/SurveyActiveDialog.vue";
import PlanRequiredDialog from "@/components/common/PlanRequiredDialog";
import DialogBackHandler from "./components/common/DialogBackHandler.vue";
import BetaBadge from "@/components/common/BetaBadge";
import HistoryToast from "@/components/common/HistoryToast";
import BetaGate from "@/components/common/BetaGate";
import safeArea from '@/plugins/safeArea'

const ADMIN_EMAILS = (() => {
    try {
        return JSON.parse(process.env.VUE_APP_ADMIN_EMAILS || '[]')
    } catch (e) {
        return []
    }
})()
const IS_BETA = process.env.VUE_APP_BETA === 'true'
const BETA_ALLOWED_EMAILS = (() => {
    try {
        return JSON.parse(process.env.VUE_APP_BETA_ALLOWED_EMAILS || '[]').map(e => String(e).toLowerCase())
    } catch (e) {
        return []
    }
})()
const FULL_WIDTH_ROUTES = ['login', 'register', 'auth-action', 'confirm-mail', 'select-profile', 'basic-info', 'coach-company-invitation']
const NO_PADDING_ROUTES = [
    'student.coaches',
    'student.coach-profile',
    'auth-action',
    'login',
    'register',
    'confirm-mail',
    'select-profile',
    'basic-info',
    'coach-company-invitation',
    'coach.statistics',
    'company.statistics',
    'company.business',
    'coach.evaluations',
    'company.evaluations',
    'student.statistics',
    'student.routine.detail',
    'student.routine.competence.detail',
    'coach.routines.detail',
    'company.routines.detail',
    'coach.routines.program.detail',
    'company.routines.program.detail',
    'coach.routines.program.detail.routine',
    'company.routines.program.detail.routine',
]
const ACCEPT_ACCESS_ROUTES = ['splash', 'coach.credit-card', 'coach.select-plan', 'company.credit-card', 'company.select-plan']

export default {
    name: 'App',
    components: {
        UpdateApp,
        OverlayUpdate,
        DisabledAccount,
        Toolbar,
        NavigationDrawer,
        AdminNavigationDrawer,
        AdminShortcut,
        HomeAgendaPanel,
        CompanyHomePanel,
        CompanyAdminPanel,
        StudentsChatPanel,
        BottomNavigation,
        KimsaToast,
        KimsaInstallToast,
        KimsaUpdateToast,
        OverlayReconnect,
        SurveyActiveDialog,
        PlanRequiredDialog,
        DialogBackHandler,
        BetaBadge,
        BetaGate,
        HistoryToast,
    },
    data() {
        return {
            needUpdateApp: false,
        }
    },
    computed: {
        ...mapState(['firstLoadApp', 'config', 'dialogStack']),
        meta(vm = this) {
            return vm.$route.meta
        },
        dialogOpen(vm = this) {
            return vm.dialogStack.length > 0
        },
        drawer(vm = this) {
            return !!vm.meta.drawer
        },
        adminDrawer(vm = this) {
            return !!vm.meta.adminDrawer
        },
        isAdminUser(vm = this) {
            const currentUserEmail = vm.currentUser?.email || null
            return !!currentUserEmail && ADMIN_EMAILS.includes(currentUserEmail)
        },
        isHomeRoute(vm = this) {
            return ['coach.home', 'student.home', 'company.home'].includes(vm.$route.name)
        },
        toolbar(vm = this) {
            return !!vm.meta.toolbar
        },
        footer(vm = this) {
            return !!vm.meta.footer
        },
        fullWidth(vm = this) {
            return FULL_WIDTH_ROUTES.includes(vm.$route.name)
        },
        appClass(vm = this) {
            const hasMainDrawer = vm.drawer && !vm.isMobile
            const hasAdminDrawer = vm.adminDrawer && vm.isAdminUser && !vm.isMobile
            return {
                'drawer-overlay': hasMainDrawer || hasAdminDrawer,
                'drawer-overlay--double': hasMainDrawer && hasAdminDrawer,
                'has-right-panel': vm.rightPanel && !vm.isMobile
            }
        },
        fullbleedDesktop(vm = this) {
            return vm.fullWidth && !vm.isMobile
        },
        noPaddingRoutes(vm = this) {
            return NO_PADDING_ROUTES.includes(vm.$route.name)
        },
        lockScroll(vm = this) {
            if (vm.isMobile || vm.fullWidth) return false
            return !!vm.meta.toolbar
        },
        rightPanel(vm = this) {
            return vm.meta.rightPanel || null
        },
        acceptAccessRoutes(vm = this) {
            return ACCEPT_ACCESS_ROUTES.includes(vm.$route.name)
        },
        isBetaBlocked(vm = this) {
            if (!IS_BETA || !BETA_ALLOWED_EMAILS.length) return false
            const email = vm.currentUser?.email?.toLowerCase() || null
            if (!email) return false
            return !BETA_ALLOWED_EMAILS.includes(email)
        },
        hasPaymentFailed(vm = this) {
            let accountType = vm.account?.accountType || ''
            if (accountType !== 'coach' && accountType !== 'company') return false
            if (!Boolean(vm.account?.stripeAccount?.currentSubscription?.paymentFailed)) return false
            let date = vm.account?.stripeAccount?.currentSubscription?.nextPaymentAttempt || null
            if (!date || (date && !vm.$moment(date * 1000).isBefore())) return false
            if (skipsBilling(vm.account)) return false
            return true
        },
        hasNoPlan(vm = this) {
            if (vm.paymentsEnabled) return false
            let accountType = vm.account?.accountType || ''
            if (accountType !== 'coach' && accountType !== 'company') return false
            if (skipsBilling(vm.account)) return false
            return !vm.account?.stripeAccount?.currentSubscription?.id
        },
        currentVersionApp(vm = this) {
            if (vm.isIosApp) return Number(vm.config?.version?.coaches_ios || 0)
            return Number(vm.config?.version?.coaches || 0)
        }
    },
    mounted() {
        if (!Capacitor.isNativePlatform()) return
        this.$nextTick(() => {
            requestAnimationFrame(async () => {
                await SplashScreen.hide().catch(() => {})
                await StatusBarManager.refresh()
                StatusBarManager.settle()
            })
        })
    },
    async created() {
        const vm = this

        vm._onWindowScroll = () => { if (!vm.dialogOpen) vm._lastScrollY = window.scrollY }
        window.addEventListener('scroll', vm._onWindowScroll, { passive: true })

        await safeArea.init()
        Screen.lockPortrait()

        if (!getStoredLocale()) {
            const deviceLocale = await detectDeviceLocale()
            if (!getStoredLocale() && !vm.account?.language) {
                applyLocale(deviceLocale, vm.$vuetify, { persist: false })
            }
        }

        vm._backButtonListener = await CapacitorApp.addListener('backButton', ({ canGoBack }) => {
            if (vm.dialogOpen) {
                vm.$store.commit('popDialog');
                return;
            }
            if (vm.$route?.name === `${vm.accountType}.home` || !canGoBack) {
                CapacitorApp.exitApp();
            } else {
                vm.$router.back();
            }
        });

        vm._appUrlOpenListener = await CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
            if (!url || !APP_SCHEMES.some(scheme => url.indexOf(`${scheme}://`) === 0)) return
            try {
                const user = firebase.auth().currentUser
                if (user) await user.reload()
            } catch { /* noop */ }
            vm.$router.replace({ name: 'splash', replace: true }).catch(() => {})
        });

        await vm.checkVersionUpdate()

        let firstAuthEvent = true
        vm._authUnsubscribe = firebase.auth().onAuthStateChanged(async user => {
            if (user) {
                await vm.initUser(user)
                await vm.initNotifications()
            } else {
                await vm.unbindUserAccount()
                const credentials = await Storage.getCredentials()

                if (credentials) {
                    try {
                        await firebase.auth().signInWithEmailAndPassword(credentials.email, credentials.password)
                        await vm.initUser(firebase.auth().currentUser)
                    } catch (err) {
                        console.log('autologin error', err)
                        await vm.setCurrentUser(null)
                        vm.setLoadingUser(false)
                    }
                } else {
                    vm.cleanDataStorage()
                    await Storage.setBrandLogo(null)
                    vm.setBrandLogo(null)
                    await vm.setCurrentUser(null)
                    vm.setLoadingUser(false)
                }
            }
            if (firstAuthEvent) {
                firstAuthEvent = false
                vm.setFirstLoadApp()
            }
        })
    },
    methods: {
        ...mapMutations(['setLoadingUser', 'setFirstLoadApp', 'setBrandLogo']),
        ...mapActions(['initUser', 'bindConfig', 'unbindUserAccount', 'setCurrentUser', 'cleanDataStorage']),
        async checkVersionUpdate() {
            const vm = this
            if (Capacitor.isNativePlatform()) {
                if (!vm.config) await vm.bindConfig()
                vm.needUpdateApp = vm.currentVersionApp > vm.versionAppCode
            }
        },
        async initNotifications() {
            const vm = this
            if (!vm.currentUser) return

            await Notifications.initListeners({
                onActionPerformed: (notification) => {
                    if (notification.actionId === 'tap' && notification.notification?.data?.path) {
                        resolveNotificationRoute(notification.notification.data.path)
                    }
                },
                onLocalActionPerformed: (notification) => {
                    if (notification.actionId === 'tap' && notification.notification?.extra?.path) {
                        resolveNotificationRoute(notification.notification.extra.path)
                    }
                },
                onReceived: (notification) => {
                    if (notification.data?.assignedRoutine) {
                        try {
                            vm.scheduleRoutineLocalNotification(JSON.parse(notification.data.assignedRoutine))
                        } catch (err) {
                            console.log('assignedRoutine parse err', err)
                        }
                    }
                },
                onRegisterError: (err) => console.log('push registrationError', err),
            })

            let permission = await Notifications.getPermissionState()
            if (permission === 'prompt' || permission === 'prompt-with-rationale') {
                permission = await Notifications.requestPermissions()
            }

            if (permission !== 'granted') {
                vm.warnPushPermissionDenied()
                return
            }

            await Notifications.register(async (token) => {
                if (!vm.currentUser) return
                try {
                    await saveDeviceToken(vm.currentUser.uid, token)
                } catch (err) {
                    console.log('saveDeviceToken err', err)
                }
            })

            const token = vm.isIosApp ? null : await Notifications.getToken()
            if (token && vm.currentUser) {
                try {
                    await saveDeviceToken(vm.currentUser.uid, token)
                } catch (err) {
                    console.log('saveDeviceToken err', err)
                }
            }

            vm.reconcileRoutineReminders()
        },
        warnPushPermissionDenied() {
            const vm = this
            try {
                if (window.sessionStorage.getItem('push.denied.warned') === '1') return
                window.sessionStorage.setItem('push.denied.warned', '1')
            } catch { /* sessionStorage unavailable */ }
            vm.toast(vm.$t('common.push_permission_denied'), 'warning')
        },
        scheduleRoutineLocalNotification(assignedRoutine) {
            const vm = this
            if (!assignedRoutine?.rawDate?.full) return
            const at = vm.$moment(assignedRoutine.rawDate.full).add(7, 'h').toDate()
            if (at.getTime() <= Date.now()) return
            LocalNotifications.schedule({
                notifications: [{
                    title: vm.appName,
                    body: vm.$t('common.have_routine_to_complete'),
                    id: vm.deterministicNotificationId(assignedRoutine),
                    schedule: { at },
                    actionTypeId: "",
                    extra: {
                        kind: 'routineReminder',
                        path: `/student/routines/${assignedRoutine.id}`,
                    },
                }]
            }).catch(err => console.log('LocalNotifications.schedule err', err))
        },
        async reconcileRoutineReminders() {
            const vm = this
            try {
                if (!(await Device.isSmartPhone())) return
                const studentId = vm.currentUser?.uid
                if (!studentId) return
                if (vm.accountType && vm.accountType !== 'student') return
                const today = vm.$moment().format('YYYY-MM-DD')
                const syncKey = `${today}|${studentId}`
                if (await Storage.getRoutineRemindersSync() === syncKey) return
                const horizon = vm.$moment().add(45, 'days').format('YYYY-MM-DD')
                const snap = await db.collection('assignedRoutines')
                    .where('studentId', '==', studentId)
                    .where('rawDate.full', '>=', today)
                    .where('rawDate.full', '<=', horizon)
                    .get()
                const routines = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                const desiredIds = new Set()
                routines.forEach(routine => {
                    if (!routine?.rawDate?.full) return
                    desiredIds.add(vm.deterministicNotificationId(routine))
                    vm.scheduleRoutineLocalNotification(routine)
                })
                const pending = await LocalNotifications.getPending()
                const stale = (pending?.notifications || [])
                    .filter(n => n.extra?.kind === 'routineReminder' && !desiredIds.has(n.id))
                if (stale.length) {
                    await LocalNotifications.cancel({ notifications: stale.map(n => ({ id: n.id })) })
                }
                await Storage.setRoutineRemindersSync(syncKey)
            } catch (err) {
                console.log('reconcileRoutineReminders err', err)
            }
        },
        deterministicNotificationId(assignedRoutine) {
            const key = `${assignedRoutine.id || ''}|${assignedRoutine.rawDate?.full || ''}`
            let hash = 5381
            for (let i = 0; i < key.length; i++) {
                hash = ((hash << 5) + hash + key.charCodeAt(i)) | 0
            }
            return Math.abs(hash) || 1
        },
        holdScrollWhileOpen() {
            const vm = this
            const y = vm._lastScrollY || window.scrollY || 0
            const fix = () => {
                if (!vm.dialogOpen) return
                if (window.scrollY !== y) window.scrollTo(0, y)
                requestAnimationFrame(fix)
            }
            requestAnimationFrame(fix)
        },
    },
    watch: {
        async account(val, oldVal) {
            const vm = this
            if (!getStoredLocale() && val?.language) {
                applyLocale(val.language, vm.$vuetify)
            }
            if (val?.email && !val.language && vm.currentUser?.uid) {
                db.collection('users').doc(vm.currentUser.uid).update({ language: vm.$i18n.locale }).catch(() => {})
            }
            let subscription = val?.stripeAccount?.currentSubscription || null
            let oldSubscription = oldVal?.stripeAccount?.currentSubscription || null
            if (subscription && subscription.id !== oldSubscription?.id) {
                await vm.$store.dispatch('bindSubscription', subscription.id)
            }
        },
        currentVersionApp() {
            this.checkVersionUpdate()
        },
        fullbleedDesktop: {
            immediate: true,
            handler(val) {
                document.documentElement.classList.toggle('fullbleed-html', val)
            }
        },
        lockScroll: {
            immediate: true,
            handler(val) {
                document.documentElement.classList.toggle('app-scroll-lock', val)
            }
        },
        dialogOpen(active) {
            const vm = this
            if (active && vm.isMobile) vm.holdScrollWhileOpen()
        }
    },
    beforeDestroy() {
        if (this._authUnsubscribe) this._authUnsubscribe()
        if (this._backButtonListener?.remove) this._backButtonListener.remove()
        if (this._appUrlOpenListener?.remove) this._appUrlOpenListener.remove()
        if (this._onWindowScroll) window.removeEventListener('scroll', this._onWindowScroll)
        safeArea.destroy()
    }
}
</script>

<style>
.no-padding {
    padding: 0;
}

html.fullbleed-html {
    overflow: hidden;
}

html.app-scroll-lock {
    overflow: hidden;
}

html.scroll-locked,
html.scroll-locked body {
    overflow: hidden;
}

html.app-scroll-lock .v-main {
    height: calc(100vh - 64px);
    margin-top: 64px;
    padding-top: 0 !important;
    overflow-y: auto;
}

.background-app {
    background: var(--v-bg-base);
}

html.overflow-y-hidden.overflow-y-hidden {
    overflow-y: auto !important;
}

.bottom-row {
    flex: 1;
    padding: 5px;
    display: flex;
    flex-direction: row;
}

.bottom-button {
    flex: 1;
    min-height: 40px;
    z-index: 6;
}

.container-bottom-button {
    flex-direction: column;
    position: fixed;
    display: flex;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 5;
    padding: 10px;
    background-color: rgba(255, 255, 255, .9);
    padding-bottom: calc(10px + var(--safe-area-inset-bottom, 0px)) !important
}

.label {
    font-size: 15px;
    color: var(--v-txtSecondary-base);
}

::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

::-webkit-scrollbar-track {
    background: var(--v-surface2-base);
    border-radius: 4px;
}

::-webkit-scrollbar-corner {
    background: var(--v-surface2-base);
}

::-webkit-scrollbar-thumb {
    background: var(--v-outline2-base);
    border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
    background: var(--v-txtMuted-base);
}

.v-skeleton-loader__bone {
    background: var(--v-surface2-base) !important;
}

.v-skeleton-loader__bone::after {
    background: linear-gradient(90deg, transparent 0%, var(--v-outline2-base) 50%, transparent 100%) !important;
}

.v-menu__content {
    background: var(--v-surface-base) !important;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.28), 0 0 0 1px var(--v-outline-base) !important;
    border-radius: 12px !important;
}

.v-menu__content .v-list,
.v-menu__content .v-select-list {
    background: transparent !important;
}

.v-tooltip__content {
    background: var(--v-surface-base) !important;
    color: var(--v-txtPrimary-base) !important;
    border: 1px solid var(--v-outline-base) !important;
    border-radius: 10px !important;
    padding: 8px 12px !important;
    font-size: 12px;
    font-weight: 500;
    opacity: 1 !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35) !important;
}

/* Date/time pickers inside menus inherit the menu background */
.v-menu__content .v-picker,
.v-menu__content .v-picker__body,
.v-menu__content .v-date-picker-table,
.v-menu__content .v-time-picker-clock__container {
    background: transparent !important;
}

.v-menu__content .v-date-picker-table .v-btn,
.v-menu__content .v-date-picker-header__value button,
.v-menu__content .v-date-picker-years li {
    color: var(--v-txtPrimary-base);
}

.v-menu__content .v-date-picker-table th {
    color: var(--v-txtMuted-base);
}

.v-menu__content .v-date-picker-table .v-btn:not(.v-btn--active):hover,
.v-menu__content .v-date-picker-years li:hover {
    background: var(--v-surface2-base) !important;
}

.v-menu__content .v-date-picker-header__value button:hover {
    background: var(--v-surface2-base);
    border-radius: 6px;
}

.theme--light .v-menu__content .v-date-picker-table .v-btn.v-btn--active {
    color: #FFFFFF !important;
}

.theme--dark .v-menu__content .v-date-picker-table .v-btn.v-btn--active {
    color: #0C0E12 !important;
}

input[type="number"]::-webkit-inner-spin-button {
    width: 14px;
    margin-left: -14px;
}

.theme--dark input[type="number"] {
    color-scheme: dark;
}

.theme--light input[type="number"] {
    color-scheme: light;
}

.drawer-overlay .v-main {
    padding-left: 56px !important;
    transition: none !important;
}
.drawer-overlay .v-app-bar {
    left: 56px !important;
    width: calc(100% - 56px) !important;
    transition: none !important;
}
.drawer-overlay--double .v-main {
    padding-left: 112px !important;
}
.drawer-overlay--double .v-app-bar {
    left: 112px !important;
    width: calc(100% - 112px) !important;
}
.has-right-panel.drawer-overlay .v-app-bar {
    width: calc(100% - 56px - 300px) !important;
}
.has-right-panel.drawer-overlay--double .v-app-bar {
    width: calc(100% - 112px - 300px) !important;
}
@media (max-width: 960px) {
    .v-main {
        padding-right: 0 !important;
    }
}
.main-safe-bottom .v-main__wrap {
    padding-bottom: var(--safe-area-inset-bottom, 0px);
}
.drawer-overlay .v-navigation-drawer {
    z-index: 7 !important;
}
.drawer-overlay .v-navigation-drawer--right {
    z-index: 9 !important;
}
.drawer-overlay .v-navigation-drawer--right.v-navigation-drawer--temporary {
    z-index: 200 !important;
}
.drawer-overlay .v-dialog .v-app-bar,
.drawer-overlay .v-navigation-drawer .v-app-bar {
    left: 0 !important;
    width: 100% !important;
}

@property --theme-reveal-radius {
    syntax: '<length>';
    inherits: true;
    initial-value: 0px;
}

::view-transition-old(root),
::view-transition-new(root) {
    animation: none;
    mix-blend-mode: normal;
}

::view-transition-new(root) {
    clip-path: circle(var(--theme-reveal-radius, 0px) at var(--theme-reveal-x, 50%) var(--theme-reveal-y, 0px));
}

::view-transition-old(root) {
    z-index: 1;
}

::view-transition-new(root) {
    z-index: 2;
}

html.theme-transition .v-application,
html.theme-transition .v-application * {
    transition: background-color .3s ease, color .3s ease, border-color .3s ease;
}
</style>
